import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { getEventReadinessItems } from '../lib/eventReadiness';

export default function EventReadinessPanel({
  readiness,
  eventType = 'wedding',
  event = null,
  compact = false,
  showChecklist = false,
}) {
  const items = useMemo(
    () => getEventReadinessItems(readiness, eventType, event),
    [event, eventType, readiness],
  );
  const requiredItems = items.filter((item) => item.required);
  const progress = Math.min(100, Math.max(0, readiness?.progress_percent || 0));
  const nextRequiredIndex = requiredItems.findIndex((item) => !item.complete);
  const completed = readiness?.completed_required
    ?? requiredItems.filter((item) => item.complete).length;
  const total = readiness?.total_required ?? requiredItems.length;

  return (
    <section className={`qc-panel qc-readiness ${compact ? 'qc-readiness--compact' : ''}`} aria-labelledby="event-readiness-title">
      <div className="qc-readiness__header">
        <div className="qc-readiness__summary">
          <p id="event-readiness-title">Getting Started</p>
          <span>{completed} out of {total} essentials complete</span>
        </div>
        {compact && <strong className="qc-readiness__percent">{progress}%</strong>}
      </div>
      <div
        className="qc-readiness__track"
        role="progressbar"
        aria-label="Getting Started"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={progress}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      {(!compact || showChecklist) && (nextRequiredIndex === -1 ? (
        <div className="qc-readiness__complete">
          <CheckCircle2 size={18} />
          All essentials are ready
        </div>
      ) : (
        <ol className="qc-readiness__checklist">
          {requiredItems.map((item, index) => {
            const isCurrent = !item.complete && index === nextRequiredIndex;
            const stateClass = item.complete
              ? 'is-complete'
              : isCurrent
                ? 'is-current'
                : 'is-future';

            return (
              <li key={item.id} className={stateClass}>
                <Link to={item.path} aria-current={isCurrent ? 'step' : undefined}>
                  {item.complete ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                  <span>{item.title}</span>
                  {isCurrent && <small>Next</small>}
                  {isCurrent && <ArrowUpRight size={15} />}
                </Link>
              </li>
            );
          })}
        </ol>
      ))}
    </section>
  );
}
